import React from 'react';
import { Box, Typography } from '@mui/material';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import HomeRepairServiceOutlinedIcon from '@mui/icons-material/HomeRepairServiceOutlined';
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import { rentalUnderwritingEnabled } from '../../config/featureFlags';
import { hoverLift } from './landingStyles';

const features = [
  {
    title: 'Deal analysis',
    body: 'Profit, cash required, project return, and break-even from the assumptions you enter.',
    icon: CalculateOutlinedIcon,
  },
  {
    title: 'Rehab + financing',
    body: 'Purchase, rehab, loan, and holding costs stay together on the deal.',
    icon: HomeRepairServiceOutlinedIcon,
  },
  {
    title: 'Comparable sales',
    body: 'Add sold properties yourself or import them when you already have a list.',
    icon: CompareArrowsOutlinedIcon,
  },
  {
    title: 'Automatic comp search',
    body: 'Look up recent sold properties and compare them with the ARV you are using.',
    icon: SearchOutlinedIcon,
  },
  {
    title: 'Risk/stress testing',
    body: 'See the model when sale price, rehab, or holding time moves against you.',
    icon: ShieldOutlinedIcon,
  },
  {
    title: 'Modeled Deal Grade + Readiness',
    body: 'A modeled grade and a checklist of what is still incomplete. Not a recommendation to buy.',
    icon: FactCheckOutlinedIcon,
  },
  {
    title: 'AI Deal Review',
    body: 'Strengths, concerns, and next steps drawn from the evidence saved on the deal.',
    icon: RateReviewOutlinedIcon,
  },
];

const rentalFeature = {
  title: 'Buy & Hold and BRRRR',
  body: 'Underwrite rent, operating costs, and financing, or model rehab and the cash left after refinance. These are saved models, not a lender approval or a recommendation to buy.',
  icon: HomeWorkOutlinedIcon,
};

const FeatureCallouts: React.FC = () => {
  const visible = rentalUnderwritingEnabled()
    ? [features[0], rentalFeature, ...features.slice(1)]
    : features;
  return (
    <Box component="section" aria-labelledby="features-title">
      <Typography id="features-title" variant="h4" component="h2" sx={{ fontWeight: 800, mb: 3 }}>
        What you can skout
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
          gap: 2,
        }}
      >
        {visible.map((feature) => {
          const Icon = feature.icon;
          return (
            <Box
              key={feature.title}
              sx={{
                p: 2.5,
                borderRadius: 2,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderTopWidth: 3,
                borderTopColor: 'brand.yellow',
                ...hoverLift,
              }}
            >
              <Icon
                sx={{
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? theme.palette.brand.yellow : theme.palette.brand.navy,
                  mb: 1,
                }}
                aria-hidden
              />
              <Typography component="h3" variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>
                {feature.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {feature.body}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default FeatureCallouts;
